import { type ParentTaskType, type SubTaskType } from "@repo/api";
import useSWR, { mutate } from "swr";

import * as fetcher from "../fetcher";
import { mutateWithStartKey } from "../mutateWithStartKey";

export type PrecallReason = "WRONG_CONTACT" | "CUSTOMER_NO_RESPONSE" | "OTHER";
export type PrecallResult = "SUCCESSFUL" | "FAILED" | "DID_NOT_CALL";

export enum HistoryEntryTypeEnum {
  GENERIC = "GENERIC",
  // Appointment
  APPOINTMENT_ASSIGNED = "APPOINTMENT_ASSIGNED",
  APPOINTMENT_CANCELLED = "APPOINTMENT_CANCELLED",
  APPOINTMENT_COMPLETED = "APPOINTMENT_COMPLETED",
  APPOINTMENT_CREATED = "APPOINTMENT_CREATED",
  APPOINTMENT_NO_SHOW = "APPOINTMENT_NO_SHOW",
  APPOINTMENT_PAID = "APPOINTMENT_PAID",
  APPOINTMENT_REASSIGNED = "APPOINTMENT_REASSIGNED",
  APPOINTMENT_RESCHEDULED = "APPOINTMENT_RESCHEDULED",
  APPOINTMENT_STARTED = "APPOINTMENT_STARTED",
  APPOINTMENT_DETACHED = "APPOINTMENT_DETACHED",
  APPOINTMENT_ATTACHED = "APPOINTMENT_ATTACHED",
  FOLLOWUP_APPOINTMENT_CREATED = "FOLLOWUP_APPOINTMENT_CREATED",
  // Communication
  COMMUNICATION_FAILED = "COMMUNICATION_FAILED",
  COMMUNICATION_SENT = "COMMUNICATION_SENT",
  // Consent
  CONSENT_UPDATE = "CONSENT_UPDATE",
  CUSTOMER_FOLLOWUP_CONSENT_OPT_OUT = "CUSTOMER_FOLLOWUP_CONSENT_OPT_OUT",
  // Notes
  CUSTOM_NOTES = "CUSTOM_NOTES",
  REMARK_UPDATE = "REMARK_UPDATE",
  // Note actions (companion audit entries)
  CUSTOM_NOTE_EDITED = "CUSTOM_NOTE_EDITED",
  CUSTOM_NOTE_PINNED = "CUSTOM_NOTE_PINNED",
  CUSTOM_NOTE_UNPINNED = "CUSTOM_NOTE_UNPINNED",
  // Draft order
  DRAFT_ORDER_CONVERTED_UPDATE = "DRAFT_ORDER_CONVERTED_UPDATE",
  DRAFT_ORDER_DELETED = "DRAFT_ORDER_DELETED",
  DRAFT_ORDER_IMPORTED = "DRAFT_ORDER_IMPORTED",
  DRAFT_ORDER_CONVERTED_TO_QUOTATION = "DRAFT_ORDER_CONVERTED_TO_QUOTATION",
  // File
  FILE_UPLOADED = "FILE_UPLOADED",
  FILE_DELETED = "FILE_DELETED",
  FILE_DOWNLOADED = "FILE_DOWNLOADED",
  FILE_SHARED = "FILE_SHARED",
  FILE_UNSHARED = "FILE_UNSHARED",
  // Update
  CLASSIFICATION_UPDATE = "CLASSIFICATION_UPDATE",
  HFB_UPDATE = "HFB_UPDATE",
  INTEREST_SERVICES_UPDATE = "INTEREST_SERVICES_UPDATE",
  PROJECT_VALUE_UPDATE = "PROJECT_VALUE_UPDATE",
  TITLE_UPDATE = "TITLE_UPDATE",
  EXPECTED_PURCHASE_DATE = "EXPECTED_PURCHASE_DATE",
  // Order
  ORDER_DELETED = "ORDER_DELETED",
  ORDER_IMPORTED = "ORDER_IMPORTED",
  ORDER_UPDATE = "ORDER_UPDATE",
  ORDER_CANCELLED = "ORDER_CANCELLED",
  ORDER_COMPLETED = "ORDER_COMPLETED",
  ORDER_ASSOCIATED = "ORDER_ASSOCIATED",
  ORDER_CONVERTED = "ORDER_CONVERTED",
  DUPLICATE_ORDER_ASSOCIATED = "DUPLICATE_ORDER_ASSOCIATED",
  // Waiting list order
  WAITING_LIST_RESTOCKED = "WAITING_LIST_RESTOCKED",
  // Planner
  PLANNER_ASSOCIATED = "PLANNER_ASSOCIATED",
  PLANNER_DELETED = "PLANNER_DELETED",
  PLANNER_CONVERTED = "PLANNER_CONVERTED",
  PLANNER_CONVERTED_TO_QUOTATION = "PLANNER_CONVERTED_TO_QUOTATION",
  PLANNER_CONVERTED_TO_DRAFT_ORDER = "PLANNER_CONVERTED_TO_DRAFT_ORDER",
  // Status
  PROJECT_CREATED = "PROJECT_CREATED",
  STATUS_UPDATE_ASSIGNED = "STATUS_UPDATE_ASSIGNED",
  STATUS_UPDATE_CLOSED_LOST = "STATUS_UPDATE_CLOSED_LOST",
  STATUS_UPDATE_CLOSED_WON = "STATUS_UPDATE_CLOSED_WON",
  STATUS_UPDATE_IN_PROGRESS = "STATUS_UPDATE_IN_PROGRESS",
  // Quotation
  QUOTATION_ASSOCIATED = "QUOTATION_ASSOCIATED",
  QUOTATION_DELETED = "QUOTATION_DELETED",
  QUOTATION_CANCELLED = "QUOTATION_CANCELLED",
  QUOTATION_CONVERTED = "QUOTATION_CONVERTED",
  // Task
  TASK_UPDATE = "TASK_UPDATE",
  TASK_CREATED = "TASK_CREATED",
  TASK_REMOVED = "TASK_REMOVED",
  // B2B
  B2B_ENR_FULFILLMENT_UPDATE = "B2B_ENR_FULFILLMENT_UPDATE",
  // spoke with customer
  CUSTOMER_CALLED_IN = "CUSTOMER_CALLED_IN",
  CALLED_THE_CUSTOMER = "CALLED_THE_CUSTOMER",
  WALK_IN_CUSTOMER = "WALK_IN_CUSTOMER",
  CUSTOMER_EMAILED = "CUSTOMER_EMAILED",
  // not grouped
  ASSIGNED_TO_TEAM = "ASSIGNED_TO_TEAM",
  STATUS_UPDATE_INVALIDATED = "STATUS_UPDATE_INVALIDATED",
  CUSTOMER_OPT_OUT = "CUSTOMER_OPT_OUT",
  CUSTOMER_PROMOTIONAL_CONSENT_OPT_OUT = "CUSTOMER_PROMOTIONAL_CONSENT_OPT_OUT",
  PROJECT_REASSIGNED = "PROJECT_REASSIGNED",
  CMP_UPDATE = "CMP_UPDATE",
  ESTIMATED_BUDGET_UPDATE = "ESTIMATED_BUDGET_UPDATE",
  INTEREST_AREA_UPDATE = "INTEREST_AREA_UPDATE",
  SCHEDULE_CALL = "SCHEDULE_CALL",
  QUOTATION_EXPIRY_COMMUNICATION_SENT = "QUOTATION_EXPIRY_COMMUNICATION_SENT",
  STAGE_CHANGE = "STAGE_CHANGE",
  INSTALLATION_QUOTATION_CREATED = "INSTALLATION_QUOTATION_CREATED",
  INSTALLATION_QUOTATION_UPDATE = "INSTALLATION_QUOTATION_UPDATE",
  SERVICE_WORK_ORDER_UPDATED = "SERVICE_WORK_ORDER_UPDATED",
  SERVICE_WORK_ORDER_CREATED = "SERVICE_WORK_ORDER_CREATED",
  // FE only.
  ORDER = "ORDER",
  APPOINTMENT = "APPOINTMENT",
  PLANNER = "PLANNER",
  FILE = "FILE",
  FIELD_UPDATE = "FIELD_UPDATE",
  CLASSIFICATION = "CLASSIFICATION",
  SCHEDULED_CALL = "SCHEDULED_CALL",
  PRE_CALL = "PRE_CALL",
}

export interface History {
  id: string;
  opportunityId: number;
  entryType: HistoryEntryTypeEnum;
  isPinned?: boolean;
  isDeleted?: boolean;
  title: string;
  body?: string;
  note?: string;
  details?: {
    new_value?: string | object;
    old_value?: string | object;
    channel?: string;
    template?: string;
    expected_delivery?: string;
    order_number?: string;
    appointment_order_number?: string;
    coworker?: string;
    error_message?: string;
    quotation_id?: string;
    task_id?: string;
    task_type?: string; // This only applies to old history entry
    parent_task_type?: ParentTaskType;
    sub_task_type?: SubTaskType;
    task_title?: string;
    updates?: HistoryUpdate[];
    note_id?: string;
    deleted_note_id?: string;
    deleted_note_entry_type?: string;
    result?: PrecallResult;
    reason?: PrecallReason;
    file_name?: string;
    items_updates?: [
      {
        expected_arrival_date?: string;
        item_no?: string;
        original_ordered_qty?: string;
        total_reserved_qty?: string;
      },
    ];
    alert_details?: { reason: string; type: string; status: string };
    // Consent
    consent_provided?: boolean;
    consent_type?: "FOLLOWUP_CONSENT"; // TODO: add new consent type
    // Planner
    planner_type?: string | null;
  };
  createdBy: string;
  createdAt: string;
  updatedBy?: string;
  updatedAt?: string;
}

interface HistoryUpdate {
  field: string;
  old_value: string;
  new_value: string;
}

export function useHistory(id?: number) {
  return useSWR<History[]>(id ? `/opportunities/${id}/history` : null, () =>
    fetcher.opportunity.get<History[]>(`/${id}/history`)
  );
}

export const createHistoryEntry = (
  entry: Partial<History>,
  opportunityId: number
) => {
  return fetcher.core
    .post(`/opportunities/${opportunityId}/history`, entry)
    .then((res) => {
      mutateWithStartKey(`/opportunities/${opportunityId}/history`);
      return res;
    });
};

export const updateHistoryEntry = (
  opportunityId: number,
  historyEntryId: string,
  update: {
    entryType: HistoryEntryTypeEnum;
    isPinned?: boolean;
    note?: string;
    updatedBy?: string;
  }
) => {
  return fetcher.opportunity
    .update(`/${opportunityId}/history/${historyEntryId}`, update)
    .then((res) => {
      mutate(
        (key) =>
          typeof key === "string" &&
          key.startsWith(`/opportunities/${opportunityId}/history`)
      );
      return res;
    });
};

export const deleteHistoryEntry = (
  opportunityId: number,
  historyEntryId: string,
  payload: {
    entryType: HistoryEntryTypeEnum.CUSTOM_NOTES;
    updatedBy?: string;
  } = { entryType: HistoryEntryTypeEnum.CUSTOM_NOTES }
) => {
  return fetcher.opportunity
    .remove(`/${opportunityId}/history/${historyEntryId}`, payload)
    .then(async (res) => {
      const contentType = res.headers.get("content-type") ?? "";
      if (contentType.includes("application/json")) {
        const payload = await res
          .clone()
          .json()
          .catch(() => null);

        const status =
          payload && typeof payload === "object" && "status" in payload
            ? String(payload.status)
            : "";
        const isErrorPayload =
          Boolean(payload) &&
          typeof payload === "object" &&
          ("group" in payload || "code" in payload) &&
          /(BAD_REQUEST|SERVER_ERROR|APPLICATION_ERROR|VALIDATION_ERROR)/i.test(
            status
          );

        if (isErrorPayload) {
          const errorMessage =
            (payload as { debugInfo?: string; message?: string }).debugInfo ||
            (payload as { message?: string }).message ||
            "Delete history entry failed";
          throw new Error(errorMessage, { cause: payload });
        }
      }

      mutate(
        (key) =>
          typeof key === "string" &&
          key.startsWith(`/opportunities/${opportunityId}/history`)
      );
      return res;
    });
};
